import { useState, useEffect, useCallback } from 'react';

import { GeolocationData } from '../types/geolocation';

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

const DEFAULT_OPTIONS: UseGeolocationOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000,
};

const formatPosition = (position: GeolocationPosition): GeolocationData => ({
  coords: {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    altitude: position.coords.altitude,
    heading: position.coords.heading,
    speed: position.coords.speed,
  },
  timestamp: position.timestamp,
});

export function useGeolocation(options: UseGeolocationOptions = {}): GeolocationData | null {
  const [location, setLocation] = useState<GeolocationData | null>(null);

  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  const requestLocation = useCallback(() => {
    if (!isSupported) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation(formatPosition(position));
      },
      (error) => {
        if (error.code === error.TIMEOUT && mergedOptions.enableHighAccuracy) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setLocation(formatPosition(position));
            },
            (fallbackError) => {
              console.error('Geolocation fallback error:', fallbackError.message);
            },
            {
              enableHighAccuracy: false,
              timeout: mergedOptions.timeout,
              maximumAge: mergedOptions.maximumAge,
            },
          );
        } else {
          console.error('Geolocation error:', error.message);
        }
      },
      {
        enableHighAccuracy: mergedOptions.enableHighAccuracy,
        timeout: mergedOptions.timeout,
        maximumAge: mergedOptions.maximumAge,
      },
    );
  }, [isSupported, mergedOptions.enableHighAccuracy, mergedOptions.timeout, mergedOptions.maximumAge]);

  useEffect(() => {
    requestLocation();
  }, []);

  return location;
}
